import { useEffect, useState } from "react";
import { BoxIcon, ImageIcon, Minus, Plus, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const productImgUrl = (asin) => `https://images-na.ssl-images-amazon.com/images/P/${asin}.01._SCLZZZZZZZ_.jpg`;

// Amazon answers an unknown ASIN with a 1x1 pixel instead of an error, so both cases count as "no image".
const imageProps = (onMissing) => ({
  onError: onMissing,
  onLoad: (e) => { if (e.target.naturalWidth <= 1) onMissing(); },
});

// Centered lightbox with +/- zoom so small on-image text (labels, sizes) can be read clearly.
// The product name, ASIN and FNSKU sit under the picture to compare it with the label in hand.
export function ProductImageLightbox({ asin, name, fnsku, onClose }) {
  const { t } = useLang();
  const [zoom, setZoom] = useState(1);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const zoomButton = "w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-100 disabled:opacity-40";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={name}
        className="relative bg-white rounded-2xl shadow-2xl flex flex-col"
        style={{ width: "min(92vw, 720px)", height: "min(88vh, 720px)" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-1.5">
            <button type="button" className={zoomButton} disabled={zoom <= 1} aria-label="−"
              onClick={() => setZoom(z => Math.max(1, +(z - 0.5).toFixed(1)))}>
              <Minus className="w-4 h-4" />
            </button>
            <span className="text-xs font-mono text-slate-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button type="button" className={zoomButton} disabled={zoom >= 4} aria-label="+"
              onClick={() => setZoom(z => Math.min(4, +(z + 0.5).toFixed(1)))}>
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <button type="button" onClick={onClose} aria-label={t("common.close")}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {missing ? (
            <div className="text-center text-slate-400">
              <BoxIcon className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{t("product.image_missing")}</p>
            </div>
          ) : (
            <img src={productImgUrl(asin)} alt={name} {...imageProps(() => setMissing(true))}
              className={zoom === 1 ? "max-w-full max-h-full object-contain" : ""}
              style={zoom !== 1 ? { width: `${zoom * 100}%`, height: "auto" } : undefined} />
          )}
        </div>
        <div className="px-4 py-2.5 border-t border-slate-100 shrink-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
          <p className="text-xs text-slate-500 font-mono">ASIN: {asin}{fnsku && ` · FNSKU: ${fnsku}`}</p>
        </div>
      </div>
    </div>
  );
}

// Small icon button for lists: the picture is fetched from Amazon only when it is clicked.
export function ProductImageButton({ asin, name, fnsku }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  if (!asin) return null;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={t("product.show_image")} aria-label={`${t("product.show_image")}: ${name}`}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">
        <ImageIcon className="h-4 w-4" />
      </button>
      {open && <ProductImageLightbox asin={asin} name={name} fnsku={fnsku} onClose={() => setOpen(false)} />}
    </>
  );
}

// Square 88x88 product thumbnail; click opens the lightbox.
export default function ProductImageThumb({ asin, alt, fnsku }) {
  const [missing, setMissing] = useState(false);
  const [open, setOpen] = useState(false);

  if (!asin) return null;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={alt}
        className="shrink-0 w-[88px] h-[88px] rounded-lg border border-slate-200 bg-white p-1 hover:border-blue-300 transition-colors">
        {missing ? (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <BoxIcon className="w-8 h-8" />
          </div>
        ) : (
          <img src={productImgUrl(asin)} alt={alt} className="w-full h-full object-contain" {...imageProps(() => setMissing(true))} />
        )}
      </button>
      {open && !missing && <ProductImageLightbox asin={asin} name={alt} fnsku={fnsku} onClose={() => setOpen(false)} />}
    </>
  );
}
